import { getAgentSessionControls } from "@/modules/sessions/requests/index.ts";
import {
  AgentSessionControl,
  AgentSessionStatus,
  AgentSessionStepType,
  MultiVendorAgentSession,
} from "@/modules/sessions/types";
import { v4 as uuid } from "uuid";
import {
  getAgentData,
  getAgentSessionData,
  getAgentStateActions,
} from "../states";
import {
  AGENT_TYPES,
  AgentData,
  AgentSpecificReviewResponseType,
  ComplianceStatus,
  MultiVendorAssessmentTableRow,
  MultiVendorAssessmentTypes,
  ReviewResponseData,
  ReviewResponseType
} from "../types/index.ts";
import {
  VendorRiskResponseItem,
  VendorRiskResponseMappedBySection,
  VendorRiskSectionEnum,
} from "../types/vendorAssessment.ts";
import { VendorRiskResponseItemForVendorOverview } from "../types/vendorOverviewSection.ts";
import { VendorRiskResponseItemForSecurityAssessment } from "../types/vendorSecurityAssessment.ts";
import { getRenderType } from "../utils/get-render-type";
import { getFieldTitle } from "../utils/getFieldTitle";
import { convertSnakeToTitleCase } from "../utils/snake-to-capital.ts";

export const processFileForVendorRiskReview = async (agentId: string) => {
  const currentData = getAgentData<
    AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
    MultiVendorAssessmentTypes.VENDOR_ASSESSMENT
  >(agentId);

  const vendorIntakeFormStep = currentData?.stepData.find(
    (step) => step.type === AgentSessionStepType.MULTI_VENDOR_PROFILE,
  );
  const editResponseStep = currentData?.stepData.find(
    (step) => step.type === AgentSessionStepType.EDIT_RESPONSE,
  );

  // if (!editResponseStep?.data.url) {
  //   throw new Error("Failed to fetch vendor assessment response");
  // }

  // const vendorJson = await get<VendorRiskResponseItem[]>({
  //   url: editResponseStep.data.url,
  //   isAuthRequired: false,
  // });

  const sessionControls = await getAgentSessionControls(agentId);
  const vendorJson: VendorRiskResponseItem[] = sessionControls.data?.map((data: AgentSessionControl) => data.multi_file_vendor_assessment) || [];

  let result: VendorRiskResponseMappedBySection = {
    ai_security_assessment: [],
    vendor_overview: [], 
    company_profile: [],
    vendor_supply_chain: [],
    zania_security_assessment: [],
    potential_issues: []
  };

  for (const key of Object.keys(result)) {
    result = {
      ...result,
      [key]: vendorJson.filter(
        (vendor) => vendor.section === (key as VendorRiskSectionEnum),
      ),
    };
  }



  return result;
};

export const generateFinalVendorReviewResponseData = (agentId: string) => {
  const agentData = getAgentData<
    AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
    MultiVendorAssessmentTypes.VENDOR_ASSESSMENT
  >(agentId);

  if (!agentData) {
    throw new Error("An error occurred");
  }

  const { mainData } = agentData;
  const { reviewResponseData } = mainData;

  if (!reviewResponseData) {
    throw new Error("An error occurred");
  }

  const approvedIds = mainData.approvedIds;
  const editedIds = mainData.editedIds;
  const result: VendorRiskResponseItemForSecurityAssessment[] = [];

  reviewResponseData.forEach((item, id) => {
    const newValue: VendorRiskResponseItemForSecurityAssessment["value"] = {
      ...item.find((item) => item.key === "value")?.value,
      sources: item.find((item) => (item.key as any) === "sources")?.value,
      compliance_status: item.find((item) => (item.key as any) === "compliance_status")?.value,
      observations: item.find((item) => (item.key as any) === "observations")?.value,
      gaps: item.find((item) => (item.key as any) === "gaps")?.value,
      recommendations: item.find((item) => (item.key as any) === "recommendations")?.value,
    };
    const risk: VendorRiskResponseItemForSecurityAssessment = {
      approved: approvedIds.includes(id),
      edited: editedIds.includes(id),
      id,
      value: newValue,
      section:
        (item.find((item) => item.key === "section")
          ?.value as VendorRiskSectionEnum.ai_security_assessment) ||
        VendorRiskSectionEnum.zania_security_assessment,
      key: item.find((item) => item.key === "key")?.value as string,
      question: item.find((item) => item.key === "question")?.value as string,
      edited_state: item.find((item) => item.key === "edited_state")?.value,
      reassesed: item.find((item) => item.key === "reassesed")?.value,
      selected: item.find((item) => item.key === "selected")?.value,
      uid: item.find((item) => item.key === "uid")?.value,
    };
    result.push(risk);
  });

  return result;
};

export const processVendorRiskReviewData = async (
  agentId: string,
  riskReviewData: VendorRiskResponseItemForSecurityAssessment[],
) => {
  const keysToOmit = [
    "id",
    "subcategory",
    "function",
    "category",
    "approved",
    "edited",
    "questions",
    "impact",
    "likelihood",
    "impact_label",
    "likelihood_label",
    "control_id",
    "zania_control_id",
    "justification",
    "section",
    "key",
    "confidence",
    "question",
    "value",
    "reassesed",
    "edited_state",
    "uid",
    "selected",
    "version",
    "index"
  ];

  const ids: string[] = [];
  const tableRows: MultiVendorAssessmentTableRow[] = [];
  const approvedIds: string[] = [];
  const editedIds: string[] = [];
  const reviewResponse = new Map<
    string,
    ReviewResponseData<
      keyof AgentSpecificReviewResponseType<
        AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
        MultiVendorAssessmentTypes.VENDOR_ASSESSMENT
      >
    >[]
  >(
    riskReviewData.map((risk) => {
      const id = risk.id ?? uuid();
      ids.push(id);
      const tableRow: MultiVendorAssessmentTableRow = {
        id: id,
        question: risk.question ?? "",
        questionKey: convertSnakeToTitleCase(risk.key).replace("ai ", "AI ").replace("Ai ", "AI "),
        compliant: risk.value.compliance_status as ComplianceStatus,
        confidence: risk.value.confidence ?? "",
        sources: risk.value.sources ?? [],
        status: risk.approved ? "approved" : risk.edited ? "edited" : "none",
      };

      tableRows.push(tableRow);

      if (risk.approved) {
        approvedIds.push(id);
      }

      if (risk.edited) {
        editedIds.push(id);
      }

      const orderedKeys = ["compliance_status", "observations", "gaps", "recommendations", "sources"];

      const keysForTextWithsources = ["observations", "gaps", "recommendations"];

      const orderedData = orderedKeys.map((key) => ({
        type: getRenderType(key, keysForTextWithsources.includes(key) ? "v2" : undefined),
        value: risk.value[key as keyof typeof risk.value],
        key: key as keyof AgentSpecificReviewResponseType<
          AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
          MultiVendorAssessmentTypes.VENDOR_ASSESSMENT
        >,
        title: getFieldTitle(key, key === "sources" ? "v2" : undefined),
        shouldRender: !keysToOmit.includes(key),
      }));

      Object.entries(risk).forEach(([key, value]) => {
        if (!orderedKeys.includes(key)) {
          const data: ReviewResponseData<
            keyof AgentSpecificReviewResponseType<
              AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
              MultiVendorAssessmentTypes.VENDOR_ASSESSMENT
            >
          > = {
            type: getRenderType(key),
            value: value as unknown,
            key: key as keyof AgentSpecificReviewResponseType<
              AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
              MultiVendorAssessmentTypes.VENDOR_ASSESSMENT
            >,
            title: getFieldTitle(key),
            shouldRender: !keysToOmit.includes(key),
            isDisabled: !!(key === "question"),
          };
          orderedData.push(data);
        }
      });

      return [id, orderedData];
    }),
  );

  const currentData = getAgentData<
    AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
    MultiVendorAssessmentTypes.VENDOR_ASSESSMENT
  >(agentId);

  if (!currentData) {
    throw new Error("An error occurred");
  }

  const stepData = currentData.stepData.map((step) => {
    if (step.type === AgentSessionStepType.MULTI_FILE_VENDOR_ASSESSMENT) {
      step.status = AgentSessionStatus.COMPLETE;
    }
    if (step.type === AgentSessionStepType.EDIT_RESPONSE) {
      step.status = AgentSessionStatus.INPUT_NEEDED;
    }
    return step;
  });

  const session = getAgentSessionData(agentId) as MultiVendorAgentSession;

  const agentData: Partial<
    AgentData<
      AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
      MultiVendorAssessmentTypes.VENDOR_ASSESSMENT
    >
  > = {
    agentType: AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
    sessionData: session as MultiVendorAgentSession,
    stepData,
    mainData: {
      ...currentData.mainData,
      reviewResponseData: reviewResponse,
      reviewResponseIds: ids,
      tableRows,
      searchFor: ["question"],
      searchTerm: "",
      approvedIds,
      editedIds,
    },
  };

  const { updateAgentData } = getAgentStateActions();

  updateAgentData<
    AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
    MultiVendorAssessmentTypes.VENDOR_ASSESSMENT
  >(agentId, agentData);
};


